import React from 'react'
import GuideLayout from '@/components/help/GuideLayout'
import GuideSection from '@/components/help/GuideSection'
import GuideWidget from '@/components/help/GuideWidget'
import GuideTip from '@/components/help/GuideTip'
import GuideRelatedLinks from '@/components/help/GuideRelatedLinks'
import DayOfWeekChart from '@/components/help/widgets/DayOfWeekChart'
import type { SharedProps } from '@/types/shared'

export default function DayOfWeekGuide({ currentUser, properties }: Pick<SharedProps, 'currentUser' | 'properties'>) {
  return (
    <GuideLayout
      currentUser={currentUser}
      properties={properties}
      title="Day of Week"
      subtitle="Capture weekly demand rhythms -- weekend premiums, midweek discounts."
    >
      <GuideSection title="Why Day-of-Week Pricing?">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Demand follows weekly patterns that are remarkably consistent. Leisure properties are
          busier Friday and Saturday -- guests book short breaks around the weekend. Business hotels
          peak Tuesday through Thursday when corporate travellers are on the road. Even mixed-use
          properties see a predictable weekly shape.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Day-of-week modifiers let you price these rhythms systematically instead of relying on
          last-minute adjustments. Set them once, and they automatically ensure your Friday rate
          reflects Friday demand and your Tuesday rate reflects Tuesday demand.
        </p>
      </GuideSection>

      <GuideSection title="How It Works">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Each day of the week (Monday through Sunday) has its own percentage modifier. These are
          applied at <strong className="text-foreground">stage 3 of 12</strong> in the rate
          pipeline, immediately after seasonal adjustments.
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            A modifier of <strong className="text-foreground">0%</strong> means no change -- the
            seasonally-adjusted rate passes through untouched
          </li>
          <li>
            A modifier of <strong className="text-foreground">+10%</strong> on Friday means Friday
            rates are 10% higher than the seasonally-adjusted rate
          </li>
          <li>
            A modifier of <strong className="text-foreground">-5%</strong> on Sunday means Sunday
            rates are 5% lower
          </li>
        </ul>
      </GuideSection>

      <GuideSection title="Stacking Example">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Day-of-week modifiers are applied to the running total after seasonal adjustments. They
          stack multiplicatively:
        </p>
        <div className="rounded-lg border bg-muted/50 p-4 space-y-1">
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">Base rate:</strong> £100
          </p>
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">Summer season (+20%):</strong> £100 × 1.20 = £120
          </p>
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">Saturday (+15% of £120):</strong> £120 × 1.15 = £138
          </p>
          <p className="mt-2 text-sm font-medium text-foreground">
            Final rate before demand adjustments: £138
          </p>
        </div>
        <p className="text-sm leading-relaxed text-muted-foreground">
          The day-of-week modifier applies to whatever the rate is after the seasonal stage -- not
          the original base rate. This means your weekly shape scales naturally with your seasonal
          pricing.
        </p>
      </GuideSection>

      <GuideWidget
        title="Weekly Rate Shape"
        footnote="Drag the sliders to see how day-of-week modifiers shape your weekly pricing pattern."
      >
        <DayOfWeekChart />
      </GuideWidget>

      <GuideSection title="Finding Your Pattern">
        <p className="text-sm leading-relaxed text-muted-foreground">
          The best way to find your day-of-week pattern is to look at your booking data. Which days
          sell out first? Those deserve positive modifiers. Which days consistently have empty rooms?
          Consider small negative modifiers to attract more bookings.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          As a starting point, most <strong className="text-foreground">leisure properties</strong>{' '}
          use something like:
        </p>
        <div className="rounded-lg border bg-muted/50 p-4">
          <ul className="ml-4 list-disc space-y-1 text-sm text-muted-foreground">
            <li>Monday to Thursday: flat (0%) or slightly negative (-5%)</li>
            <li>Friday: +10% to +15%</li>
            <li>Saturday: +15% to +20%</li>
            <li>Sunday: -5% to flat (0%)</li>
          </ul>
        </div>
        <p className="text-sm leading-relaxed text-muted-foreground">
          <strong className="text-foreground">Business-focused properties</strong> often see the
          opposite: midweek rates slightly higher than weekend rates. There is no universal rule --
          your data and local market tell the real story.
        </p>
      </GuideSection>

      <div className="space-y-3">
        <GuideTip type="do">
          Start conservative: plus or minus 5-10% and observe the results. Adjust after 2-4 weeks
          of data. Small changes compound with seasonal and demand adjustments.
        </GuideTip>
        <GuideTip type="dont">
          Don't set extreme swings (greater than 20%) unless your data clearly supports it. Demand
          response handles the dynamic part -- day-of-week should capture the structural pattern
          that stays consistent week to week.
        </GuideTip>
      </div>

      <GuideRelatedLinks
        links={[
          { slug: 'seasons', title: 'Seasons' },
          { slug: 'demand-response', title: 'Demand Response' },
          { slug: 'rooms-setup', title: 'Rooms Setup' },
        ]}
      />
    </GuideLayout>
  )
}
